import { useTranslation } from 'react-i18next';
import {
  groupSubtotals,
  reconcilePrice,
  type QuoteCalculationResult,
  type ReconciledPrice,
} from '@shared/calculations/pricing.ts';
import { defaultGroupOf, groupsChromeVisible } from '@shared/quotes/groups.ts';
import type { Quote } from '@shared/types/index.ts';
import { calculationInputFor } from '../../hooks/use-quote-editor.ts';
import { customerCalculation, customerPaysCents } from './plain-price.tsx';

/**
 * One group's share of the pre-tax labor, in quote order. Null when the
 * quote is still a single untouched default group — there is nothing to
 * reconcile against a headline.
 */
export function visibleGroupSubtotals(quote: Quote): number[] | null {
  const groups = quote.groups ?? [];
  if (!groupsChromeVisible(groups)) return null;
  const fallback = defaultGroupOf(groups)?.id;
  const ordered = [...groups].sort((left, right) => left.displayOrder - right.displayOrder);
  const partitions = ordered.map((group) =>
    quote.rooms.flatMap((room, index) => {
      const groupId =
        room.groupId && groups.some((candidate) => candidate.id === room.groupId) ? room.groupId : fallback;
      return groupId === group.id ? [index] : [];
    }),
  );
  return groupSubtotals(calculationInputFor(quote), partitions).map((part) => part.cents);
}

/**
 * Group subtotals plus the quote-wide lines, adding up to the same cents the
 * headline states. Null when the quote is not showing groups.
 */
export function headlineReconciliation(quote: Quote, preview: QuoteCalculationResult): ReconciledPrice | null {
  const groups = visibleGroupSubtotals(quote);
  if (!groups) return null;
  const priced = customerCalculation(quote, preview);
  return reconcilePrice(groups, priced, customerPaysCents(quote, preview.totalCents));
}

/** True when a line other than the group sum is needed to reach the total. */
export function reconciliationHasGap(lines: ReconciledPrice): boolean {
  return (
    lines.billingRoundingCents !== 0 ||
    lines.feesCents !== 0 ||
    lines.discountCents > 0 ||
    lines.frequencyDiscountCents > 0 ||
    lines.taxCents !== 0 ||
    lines.adjustmentCents !== 0
  );
}

/**
 * Subtotal, then tax / rounding / fees / discounts, then the total.
 * Rendered only when those lines are what makes the group amounts equal
 * the price at the top.
 */
export function PriceBreakdown({
  lines,
  amount,
}: {
  lines: ReconciledPrice;
  /** Formats cents the same way the group rows next to this list do. */
  amount: (cents: number) => string;
}) {
  const { t } = useTranslation();
  if (!reconciliationHasGap(lines)) return null;

  const rows: Array<{ key: string; label: string; cents: number; deduct?: boolean }> = [
    { key: 'subtotal', label: t('totalsPanel.subtotal'), cents: lines.subtotalCents },
  ];
  if (lines.billingRoundingCents !== 0) {
    rows.push({ key: 'rounding', label: t('totalsPanel.billingRounding'), cents: lines.billingRoundingCents });
  }
  if (lines.feesCents !== 0) {
    rows.push({ key: 'fees', label: t('totalsPanel.fees'), cents: lines.feesCents });
  }
  if (lines.discountCents > 0) {
    rows.push({ key: 'discount', label: t('totalsPanel.discount'), cents: lines.discountCents, deduct: true });
  }
  if (lines.frequencyDiscountCents > 0) {
    rows.push({
      key: 'frequency',
      label: t('totalsPanel.frequencyDiscount'),
      cents: lines.frequencyDiscountCents,
      deduct: true,
    });
  }
  if (lines.taxCents !== 0) {
    rows.push({ key: 'tax', label: t('totalsPanel.tax'), cents: lines.taxCents });
  }
  if (lines.adjustmentCents !== 0) {
    rows.push({ key: 'adjustment', label: t('totalsPanel.adjustment'), cents: lines.adjustmentCents });
  }

  return (
    <dl className="mt-3 space-y-2 border-t border-[color:var(--color-line)] pt-3 text-sm">
      {rows.map((row) => (
        <div key={row.key} className="flex justify-between gap-4">
          <dt className="text-[color:var(--color-muted)]">{row.label}</dt>
          <dd className="tabular">
            {row.deduct || row.cents < 0 ? '−' : ''}
            {amount(Math.abs(row.cents))}
          </dd>
        </div>
      ))}
      <div className="flex justify-between gap-4 border-t border-[color:var(--color-line)] pt-2 font-bold">
        <dt>{t('totalsPanel.total')}</dt>
        <dd className="tabular">{amount(lines.totalCents)}</dd>
      </div>
    </dl>
  );
}
